import React, { useState, useEffect, useCallback } from 'react';
import Title from '../GenericComponents/Title';
import Loader from '../Loader/Loader';
import moment from 'moment';
import Paginations from '../GenericComponents/Pagination';
import { delete_ApiManager } from '../ApiManager/ApiManager';
import * as FaIcons from 'react-icons/fa';
import swal from 'sweetalert';
import { addIntake, getAllIntakes, searchIntakes, updateIntake } from '../Services/apiServices';
import MultipleSearchs from '../GenericComponents/MultipleSearchs';
import { intakeQuarters, intakeYears } from '../../utilis/constants';
import ProviderIntakeModal from './ProviderIntakeModal';
import '../../styles/tailwind.css';
import {
  ProviderSettingsFilterToolbar,
  ProviderSettingsFilterField,
  ProviderSettingsSelect,
  ProviderSettingsButtonSecondary,
  ProviderSettingsAddButton,
  ProviderSettingsToolbarActions,
} from './ProviderSettingsUi';

function buildIntakeSearchPayload(year = '', quarter = '') {
  return {
    year: year ? [{ name: year }] : [],
    quarter: quarter ? [{ name: quarter }] : [],
  };
}

const ViewIntakes = React.memo(({ embedded = false }) => {
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [editModel, setEditModel] = useState(false);
  const [allIntakes, setAllIntakes] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [defaultMonths, setDefaultMonths] = useState('');
  const [defaultYear, setDefaultYear] = useState('');
  const [defaultQuarter, setDefaultQuarter] = useState('');
  const [editId, setEditId] = useState('');
  const [order, setOrder] = useState('ASC');

  const [filterYear, setFilterYear] = useState('');
  const [filterQuarter, setFilterQuarter] = useState('');
  const [appliedYear, setAppliedYear] = useState('');
  const [appliedQuarter, setAppliedQuarter] = useState('');
  const [isFiltered, setIsFiltered] = useState(false);

  const headers = [
    { name: '#', field: 'id', align: 'text-center' },
    { name: 'Intakes', field: 'title', align: 'text-center' },
    { name: 'Created At', field: 'created_at', align: 'text-center' },
    { name: '', field: '' },
  ];

  const getIntakes = useCallback(async (page = currentPage, pageSize = perPage) => {
    setLoading(true);
    const result = await getAllIntakes({ currentPage: page, perPage: pageSize });
    if (result.error) {
      swal({ text: result.error, icon: 'warning', dangerMode: true });
      setLoading(false);
      return;
    }

    setAllIntakes(result.data.data['data']);
    setTotalItem(result.data.data['total']);
    setPerPage(result.data.data['per_page']);
    setLastPage(result.data.data['last_page']);
    setLoading(false);
  }, [currentPage, perPage]);

  const searchWithFilters = useCallback(async (
    year,
    quarter,
    page = currentPage,
    pageSize = perPage,
  ) => {
    setLoading(true);
    const data = buildIntakeSearchPayload(year, quarter);
    const result = await searchIntakes({ currentPage: page, perPage: pageSize }, data);

    if (result.error) {
      swal({ text: result.error, icon: 'warning', dangerMode: true });
      setLoading(false);
      return;
    }

    setAllIntakes(result.data.data['data']);
    setTotalItem(result.data.data['total']);
    setPerPage(result.data.data['per_page']);
    setLastPage(result.data.data['last_page']);
    setLoading(false);
  }, [currentPage, perPage]);

  useEffect(() => {
    getIntakes(1, perPage);
  }, []);

  const handleLegacySearchCallback = useCallback(async (input1, input2) => {
    if (input1 === 'clear') {
      setIsFiltered(false);
      setFilterYear('');
      setFilterQuarter('');
      setAppliedYear('');
      setAppliedQuarter('');
      setCurrentPage(1);
      getIntakes(1, perPage);
      return;
    }

    const year = typeof input1 === 'string' ? input1 : '';
    const quarter = typeof input2 === 'string' ? input2 : '';

    setFilterYear(year);
    setFilterQuarter(quarter);
    setAppliedYear(year);
    setAppliedQuarter(quarter);
    setIsFiltered(Boolean(year || quarter));
    setCurrentPage(1);
    searchWithFilters(year, quarter, 1, perPage);
  }, [getIntakes, perPage, searchWithFilters]);

  const handlePaginationCallback = useCallback((i, newPerPage) => {
    const nextPage = newPerPage ? 1 : i;
    const nextPerPage = newPerPage || perPage;

    setCurrentPage(nextPage);
    if (newPerPage) {
      setPerPage(newPerPage);
    }

    if (isFiltered) {
      searchWithFilters(appliedYear, appliedQuarter, nextPage, nextPerPage);
    } else {
      getIntakes(nextPage, nextPerPage);
    }
  }, [
    isFiltered,
    appliedYear,
    appliedQuarter,
    perPage,
    searchWithFilters,
    getIntakes,
  ]);

  const handleAddIntakeSubmit = useCallback(async (months, quarterValue, yearValue) => {
    if (!months || !quarterValue || !yearValue) {
      swal('Missing!', 'Please enter complete information!', 'error');
      return;
    }

    const data = {
      months,
      quarter: quarterValue,
      year: yearValue,
    };

    const result = await addIntake({ data });
    if (result.error) {
      swal({ text: result.error, icon: 'warning', dangerMode: true });
      return;
    }

    setShow(false);
    if (isFiltered) {
      searchWithFilters(appliedYear, appliedQuarter, currentPage, perPage);
    } else {
      getIntakes(currentPage, perPage);
    }
  }, [isFiltered, appliedYear, appliedQuarter, currentPage, perPage, searchWithFilters, getIntakes]);

  const handleEditIntakeSubmit = useCallback(async (months, quarterValue, yearValue) => {
    const data = {
      months: months || defaultMonths,
      quarter: quarterValue || defaultQuarter,
      year: yearValue || defaultYear,
    };

    const result = await updateIntake(editId, data);
    if (result.error) {
      swal({ text: result.error, icon: 'warning', dangerMode: true });
      return;
    }

    swal('Success!', result.data.message, 'success');
    setEditModel(false);
    if (isFiltered) {
      searchWithFilters(appliedYear, appliedQuarter, currentPage, perPage);
    } else {
      getIntakes(currentPage, perPage);
    }
  }, [
    editId,
    defaultMonths,
    defaultQuarter,
    defaultYear,
    isFiltered,
    appliedYear,
    appliedQuarter,
    currentPage,
    perPage,
    searchWithFilters,
    getIntakes,
  ]);

  const deleteIntake = useCallback((title, deleteId) => {
    swal({
      title: 'Are you sure?',
      text: `You will not be able to recover (${title})`,
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        delete_ApiManager(deleteId, 'intakes', () => {
          swal(`Intake (${title}) deleted successfully`, { icon: 'success' });
          if (isFiltered) {
            searchWithFilters(appliedYear, appliedQuarter, currentPage, perPage);
          } else {
            getIntakes(currentPage, perPage);
          }
        }, (err) => console.log(err));
      }
    });
  }, [
    isFiltered,
    appliedYear,
    appliedQuarter,
    currentPage,
    perPage,
    searchWithFilters,
    getIntakes,
  ]);

  const sorting = useCallback((col) => {
    if (col) {
      const sorted = [...allIntakes].sort((a, b) =>
        order === 'ASC' ? (a[col] > b[col] ? 1 : -1) : (a[col] < b[col] ? 1 : -1)
      );
      setOrder(order === 'ASC' ? 'DEC' : 'ASC');
      setAllIntakes(sorted);
    }
  }, [allIntakes, order]);

  const handleEmbeddedFilterSearch = () => {
    setAppliedYear(filterYear);
    setAppliedQuarter(filterQuarter);
    const hasFilters = Boolean(filterYear || filterQuarter);
    setIsFiltered(hasFilters);
    setCurrentPage(1);
    if (hasFilters) {
      searchWithFilters(filterYear, filterQuarter, 1, perPage);
    } else {
      getIntakes(1, perPage);
    }
  };

  const handleEmbeddedFilterClear = () => {
    setFilterYear('');
    setFilterQuarter('');
    setAppliedYear('');
    setAppliedQuarter('');
    setIsFiltered(false);
    setCurrentPage(1);
    getIntakes(1, perPage);
  };

  let count = currentPage * perPage - perPage + 1;

  const modals = (
    <>
      <ProviderIntakeModal
        show={show}
        onClose={() => setShow(false)}
        onSubmit={handleAddIntakeSubmit}
      />
      <ProviderIntakeModal
        show={editModel}
        isEditing
        defaultMonths={defaultMonths}
        defaultQuarter={defaultQuarter}
        defaultYear={defaultYear}
        onClose={() => setEditModel(false)}
        onSubmit={handleEditIntakeSubmit}
      />
    </>
  );

  const handleEmbeddedFilterSubmit = (event) => {
    event?.preventDefault?.();
    handleEmbeddedFilterSearch();
  };

  const renderTable = () => (
    <>
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse">
            <thead>
              <tr className="bg-gray-50">
                {headers.map((head) => (
                  <th
                    key={head.field || head.name}
                    className={`px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wide text-gray-500 ${
                      head.field ? 'cursor-pointer select-none hover:text-gray-700' : ''
                    } ${head.name === '' ? 'w-28 text-right' : ''}`}
                    onClick={() => head.field && sorting(head.field)}
                  >
                    {head.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, rowIndex) => (
                  <tr key={rowIndex} className="border-t border-gray-100">
                    {[...Array(4)].map((__, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-4">
                        <div className="h-4 animate-pulse rounded bg-gray-200" style={{ width: '55%' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : allIntakes.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
                      <FaIcons.FaCalendarAlt size={40} className="text-gray-300" />
                      <h6 className="mt-3 mb-1 font-semibold text-gray-600">No intakes found</h6>
                      <p className="mb-0 text-sm text-gray-400">
                        {isFiltered
                          ? 'Try adjusting your year or quarter filters.'
                          : 'Get started by adding your first course intake.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                allIntakes.map((value) => (
                  <tr
                    key={value.id}
                    className="border-t border-gray-100 transition hover:bg-blue-50/40"
                  >
                    <td className="px-4 py-3.5 text-sm font-medium text-gray-700">{count++}</td>
                    <td className="px-4 py-3.5 text-sm font-semibold text-gray-900">{value.title}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-600">
                      {moment(value.created_at).format('DD MMM YYYY')}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          title="Edit"
                          onClick={() => {
                            setEditId(value.id);
                            setDefaultMonths(value.months);
                            setDefaultYear(value.year);
                            setDefaultQuarter(value.quarter);
                            setEditModel(true);
                          }}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border-0 bg-blue-50 text-blue-600 transition hover:bg-blue-600 hover:text-white"
                        >
                          <FaIcons.FaEdit size={13} />
                        </button>
                        <button
                          type="button"
                          title="Delete"
                          onClick={() => deleteIntake(value.title, value.id)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border-0 bg-red-50 text-red-500 transition hover:bg-red-500 hover:text-white"
                        >
                          <FaIcons.FaTrashAlt size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && allIntakes.length > 0 ? (
        <div className="mt-4">
          <Paginations
            currentPage={currentPage}
            totalItems={totalItem}
            itemsPerPage={perPage}
            lastPage={lastPage}
            onPageChange={handlePaginationCallback}
          />
        </div>
      ) : null}
    </>
  );

  if (embedded) {
    return (
      <>
        <ProviderSettingsFilterToolbar onSubmit={handleEmbeddedFilterSubmit}>
          <ProviderSettingsFilterField label="Year" htmlFor="intake-filter-year">
            <ProviderSettingsSelect
              id="intake-filter-year"
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
            >
              <option value="">Search Year</option>
              {intakeYears.map((item) => (
                <option key={item.name} value={item.name}>
                  {item.name}
                </option>
              ))}
            </ProviderSettingsSelect>
          </ProviderSettingsFilterField>
          <ProviderSettingsFilterField label="Quarter" htmlFor="intake-filter-quarter">
            <ProviderSettingsSelect
              id="intake-filter-quarter"
              value={filterQuarter}
              onChange={(e) => setFilterQuarter(e.target.value)}
            >
              <option value="">Search Quarter</option>
              {intakeQuarters.map((item) => (
                <option key={item.name} value={item.name}>
                  {item.name}
                </option>
              ))}
            </ProviderSettingsSelect>
          </ProviderSettingsFilterField>
          <ProviderSettingsToolbarActions>
            <ProviderSettingsButtonSecondary type="submit" icon={FaIcons.FaSearch}>
              Search
            </ProviderSettingsButtonSecondary>
            <ProviderSettingsButtonSecondary onClick={handleEmbeddedFilterClear}>
              Clear
            </ProviderSettingsButtonSecondary>
            <ProviderSettingsAddButton onClick={() => setShow(true)}>
              Add Intake
            </ProviderSettingsAddButton>
          </ProviderSettingsToolbarActions>
        </ProviderSettingsFilterToolbar>

        {renderTable()}
        {modals}
      </>
    );
  }

  return (
    <>
      <Title
        title="View Intakes"
        btnCol="col-lg-8 text-center pr-lg-2"
        btn
        back
        btnText="Add New Intake"
        btnClass="float-right btn btn-success"
        parentCallback={() => setShow(true)}
      />
      {loading ? <Loader /> : (
        <div className="row w-100 pl-3 pr-3 mb-5 mt-lg-2">
          <MultipleSearchs
            input1="Search Year"
            input1_type="dropdown"
            input1_multiple="no"
            input1Data={intakeYears}
            input1_filter_by="name"
            input2="Search Quarter"
            input2_type="dropdown"
            input2_multiple="no"
            input2Data={intakeQuarters}
            input2_filter_by="name"
            onSearchChange={handleLegacySearchCallback}
            allColumns={headers}
          />
          <table className="table table-bordered bg-white mb-1">
            <thead className="thead-dark">
              <tr>
                {headers.map((head) => (
                  <th key={head.field} className={head.align} onClick={() => sorting(head.field)}>
                    {head.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allIntakes.map((value) => (
                <tr key={value.id}>
                  <td className="text-center">{count++}</td>
                  <td className="text-center name-text">{value.title}</td>
                  <td className="text-center">{moment(value.created_at).format('YYYY-MM-DD')}</td>
                  <td className="text-center w-20">
                    <button
                      onClick={() => {
                        setEditId(value.id);
                        setDefaultMonths(value.months);
                        setDefaultYear(value.year);
                        setDefaultQuarter(value.quarter);
                        setEditModel(true);
                      }}
                      className="btn btn-primary m-1"
                    >
                      <FaIcons.FaEdit size={13} />
                    </button>
                    <button onClick={() => deleteIntake(value.title, value.id)} className="btn btn-danger m-1">
                      <FaIcons.FaTrashAlt size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Paginations
            currentPage={currentPage}
            totalItems={totalItem}
            itemsPerPage={perPage}
            lastPage={lastPage}
            onPageChange={handlePaginationCallback}
          />
        </div>
      )}

      {modals}
    </>
  );
});

export default ViewIntakes;
